import {
  GridLayout,
  ChatMessage as LiveKitChatMessage,
  ParticipantTile,
  TrackReferenceOrPlaceholder,
  WidgetState,
} from "@livekit/components-react"
import { Box } from "@mui/material"
import { TrackPublication } from "livekit-client"
import React, { Fragment } from "react"
import { getViewWidth } from "../../constants/styles"
import { Agent } from "../../types/Agent"
import { UserVideoLiveKit } from "../UserVideo/UserVideoLiveKit"
import CallLanguage from "./CallLanguage"
import { ChatWithAgent } from "./ChatWithAgent"
import CustomCallTimer from "./CustomCallTimer"
import { CustomChatBox } from "./CustomChatBox"
import { CustomControlsBar } from "./CustomControlsBar"

type LayoutProps = {
  agent: Agent
  tracks: TrackReferenceOrPlaceholder[]
  showChat: boolean
  audioState: boolean
  videoState: boolean
  isEmbed: boolean
  localCameraTrack: TrackPublication
  handleDisconnect: () => void
  toggleChat: () => void
  duration: number
  maxDuration: number
  closeChat: () => void
  openChat: () => void
  chatMessages: LiveKitChatMessage[]
  isSending: boolean
  send: (message: string) => Promise<LiveKitChatMessage>
  update: (
    message: string,
    originalMessageOrId: string | LiveKitChatMessage,
  ) => Promise<{
    readonly message: string
    readonly editTimestamp: number
    readonly id: string
    readonly timestamp: number
  }>
  SettingsComponent: React.ComponentType
  widgetState: WidgetState
}

export const WebLayout = React.memo(function WebLayout({
  tracks,
  showChat,
  localCameraTrack,
  handleDisconnect,
  toggleChat,
  duration,
  maxDuration,
  closeChat,
  openChat,
  chatMessages,
  isSending,
  send,
  update,
  SettingsComponent,
  widgetState,
  audioState,
  videoState,
  agent,
  isEmbed,
}: LayoutProps) {
  const topOffset = isEmbed ? 0 : "10vh"
  const sideOffset = isEmbed ? 0 : "100px"

  return (
    <Fragment>
      <Box
        className="lk-video-conference-inner"
        margin={"0 auto"}
        marginRight={"auto"}
        position={"fixed"}
        top={topOffset}
        left={sideOffset}
        right={sideOffset}
        height={isEmbed ? "100%" : `calc(100% - ${topOffset})`}
        width={isEmbed ? "100%" : `calc(100% - ${sideOffset} - ${sideOffset})`}
        zIndex={1000}
        padding={isEmbed ? 0 : { xs: "5px", sm: "5px 10px 10px 10px" }}
      >
        <GridLayout
          tracks={tracks.filter(
            (track) =>
              !track.participant.isLocal &&
              !(
                track.participant.isAgent &&
                track.participant.attributes &&
                track.participant.attributes["lk.agent.state"]
              ),
          )}
          style={{ borderRadius: isEmbed ? 0 : "12px", overflow: "hidden" }}
        >
          <ParticipantTile />
        </GridLayout>
        <UserVideoLiveKit track={localCameraTrack} />

        <CustomControlsBar
          showChat={showChat}
          disconnect={handleDisconnect}
          toggleChat={toggleChat}
          audioState={audioState}
          videoState={videoState}
        />
        <CustomCallTimer duration={duration} maxDuration={maxDuration} />
        <CallLanguage agent={agent} />
        <ChatWithAgent agent={agent} />
        <CustomChatBox
          showChat={showChat}
          closeChat={closeChat}
          openChat={openChat}
          chatMessages={chatMessages}
          isSending={isSending}
          send={send}
          update={update}
        />
      </Box>
      {SettingsComponent && (
        <div
          className="lk-settings-menu-modal"
          style={{ display: widgetState.showSettings ? "block" : "none" }}
        >
          <SettingsComponent />
        </div>
      )}
    </Fragment>
  )
})

export const MobileLayout = React.memo(function MobileLayout({
  tracks,
  showChat,
  localCameraTrack,
  handleDisconnect,
  toggleChat,
  duration,
  maxDuration,
  closeChat,
  openChat,
  chatMessages,
  isSending,
  send,
  update,
  SettingsComponent,
  widgetState,
  audioState,
  videoState,
  agent,
  isEmbed,
}: LayoutProps) {
  const topOffset = isEmbed ? 0 : "5vh"
  const sideOffset = isEmbed ? 0 : "23px"

  return (
    <Box display={"flex"} flexDirection={"column"} height={"100%"}>
      <Box flex={1} position={"relative"}>
        <Box
          className="lk-video-conference-inner"
          position={"fixed"}
          top={topOffset}
          left={sideOffset}
          right={sideOffset}
          bottom={sideOffset}
          zIndex={1000}
          height={
            isEmbed ? "100%" : `calc(100% - ${topOffset} - ${sideOffset})`
          }
          width={
            isEmbed ? "100%" : `calc(100% - ${sideOffset} - ${sideOffset})`
          }
          borderRadius={"var(--spacing-tokens-size-in-px-spacing-spacing-xl)"}
          overflow={"hidden"}
        >
          <GridLayout
            tracks={tracks.filter(
              (track) =>
                !track.participant.isLocal &&
                !(
                  track.participant.isAgent &&
                  track.participant.attributes &&
                  track.participant.attributes["lk.agent.state"]
                ),
            )}
            style={{ height: "100%", width: "100%" }}
            autoFocus
          >
            <ParticipantTile />
          </GridLayout>
          <UserVideoLiveKit track={localCameraTrack} />
          <CustomControlsBar
            showChat={showChat}
            disconnect={handleDisconnect}
            toggleChat={toggleChat}
            audioState={audioState}
            videoState={videoState}
          />
          <CustomCallTimer duration={duration} maxDuration={maxDuration} />
          <CallLanguage agent={agent} />
          <ChatWithAgent agent={agent} />
        </Box>
      </Box>
      <CustomChatBox
        showChat={showChat}
        closeChat={closeChat}
        openChat={openChat}
        chatMessages={chatMessages}
        isSending={isSending}
        send={send}
        update={update}
      />
      {SettingsComponent && (
        <div
          className="lk-settings-menu-modal"
          style={{ display: widgetState.showSettings ? "block" : "none" }}
        >
          <SettingsComponent />
        </div>
      )}
    </Box>
  )
})
